import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import { Sparkles, TrendingUp, AlertTriangle, Lightbulb, ChevronDown, X } from "lucide-react";

export type AiPosHint = {
  id: string;
  type: "insight" | "warning" | "suggestion";
  message: string;
};

type AiHintsDockProps = {
  hints: AiPosHint[];
  onDismissHint?: (hintId: string) => void;
  onDismissAll?: () => void;
};

function getIcon(type: string) {
  switch (type) {
    case "insight":
      return <TrendingUp className="h-4 w-4 shrink-0 text-primary" />;
    case "warning":
      return <AlertTriangle className="h-4 w-4 shrink-0 text-chart-4" />;
    case "suggestion":
      return <Lightbulb className="h-4 w-4 shrink-0 text-chart-2" />;
    default:
      return <Sparkles className="h-4 w-4 shrink-0 text-primary" />;
  }
}

export function AiHintsDock({ hints, onDismissHint, onDismissAll }: AiHintsDockProps) {
  const [expanded, setExpanded] = useState(false);
  const warningHints = useMemo(() => hints.filter((hint) => hint.type === "warning"), [hints]);

  if (hints.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-40 w-[min(22rem,calc(100vw-2rem))]">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.16 }}
          className="pointer-events-auto rounded-xl border border-border bg-card/95 shadow-lg backdrop-blur"
        >
          <div className="flex items-center gap-2 p-2.5">
            <AlertTriangle className="h-4 w-4 shrink-0 text-chart-4" />
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-1 py-0.5 text-left text-sm text-foreground"
            >
              <span className="truncate">
                {warningHints.length > 0
                  ? `${warningHints.length} low-stock item${warningHints.length > 1 ? "s" : ""} in view`
                  : `${hints.length} operational alert${hints.length > 1 ? "s" : ""}`}
              </span>
              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => onDismissAll?.()}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Dismiss alerts"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <AnimatePresence initial={false}>
            {expanded ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden border-t border-border"
              >
                <div className="max-h-52 overflow-y-auto p-2">
                  {hints.map((hint) => (
                    <div
                      key={hint.id}
                      className="mb-1.5 flex items-start gap-2 rounded-lg bg-muted/70 px-2 py-1.5 last:mb-0"
                    >
                      {getIcon(hint.type)}
                      <p className="min-w-0 flex-1 text-xs text-foreground">{hint.message}</p>
                      <button
                        type="button"
                        onClick={() => onDismissHint?.(hint.id)}
                        className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                        aria-label="Dismiss alert"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export function AiHintsMiniStack({ hints }: { hints: AiPosHint[] }) {
  if (hints.length === 0) return null;
  return (
    <div className="pointer-events-none fixed right-4 top-16 z-40 w-[min(18rem,calc(100vw-2rem))]">
      <AnimatePresence>
        {hints.slice(0, 2).map((hint, index) => (
          <motion.div
            key={hint.id}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.96 }}
            transition={{ delay: index * 0.04, duration: 0.14 }}
            className="pointer-events-auto mb-1.5 rounded-lg border border-border bg-card/95 px-3 py-2 shadow"
          >
            <div className="flex items-start gap-2">
              {getIcon(hint.type)}
              <p className="text-xs text-foreground">{hint.message}</p>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
